import { useCallback, useEffect, useState } from 'react';
import type { WidgetLayoutEntry } from '../widgets/types';
import WIDGET_REGISTRY, { getWidget, getWidgetsForContext } from '../widgets/registry';
import { fetchWidgetLayout, saveWidgetLayout } from '../widgets/api';

interface WidgetBoardProps {
	contextType: 'ecosystem' | 'project';
	contextId: string;
	ecosystemId: string;
	ecosystemServices?: string[];
}

export default function WidgetBoard({
	contextType,
	contextId,
	ecosystemId,
	ecosystemServices,
}: WidgetBoardProps) {
	const [layout, setLayout] = useState<WidgetLayoutEntry[] | null>(null);
	const [editMode, setEditMode] = useState(false);
	const [saving, setSaving] = useState(false);
	const [loadError, setLoadError] = useState('');
	const [settingsWidgetId, setSettingsWidgetId] = useState<string | null>(null);

	const availableWidgets = getWidgetsForContext(contextType, ecosystemId, ecosystemServices);
	const validIds = new Set(WIDGET_REGISTRY.map((w) => w.manifest.id));

	useEffect(() => {
		setLayout(null);
		setLoadError('');
		setEditMode(false);
		setSettingsWidgetId(null);

		fetchWidgetLayout(contextType, contextId, ecosystemId)
			.then(({ layout: saved }) => {
				setLayout(saved ? saved.filter((e) => validIds.has(e.widgetId)) : []);
			})
			.catch((err: Error) => {
				setLoadError(err.message);
				setLayout([]);
			});
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [contextType, contextId, ecosystemId]);

	const activeIds = new Set(layout?.map((e) => e.widgetId) ?? []);

	const toggleWidget = useCallback((widgetId: string) => {
		setLayout((prev) => {
			if (!prev) return prev;
			if (prev.some((e) => e.widgetId === widgetId)) {
				return prev.filter((e) => e.widgetId !== widgetId);
			}
			const manifest = getWidget(widgetId)?.manifest;
			return [...prev, { widgetId, size: manifest?.defaultSize ?? 'half' }];
		});
	}, []);

	const handleDone = async () => {
		if (!layout) return;
		setSaving(true);
		try {
			await saveWidgetLayout(contextType, contextId, ecosystemId, layout);
			setEditMode(false);
		} catch (err) {
			console.error('Failed to save widget layout:', err);
		} finally {
			setSaving(false);
		}
	};

	const settingsEntry = settingsWidgetId ? getWidget(settingsWidgetId) : null;
	const SettingsComponent = settingsEntry?.SettingsComponent;

	if (layout === null) {
		return (
			<div className="widget-board">
				<div className="widget-board-header">
					<span className="widget-board-title">Widgets</span>
				</div>
				<div className="widget-inner-loading">Loading widgets…</div>
			</div>
		);
	}

	return (
		<div className="widget-board">
			<div className="widget-board-header">
				<span className="widget-board-title">Widgets</span>
				{!editMode ? (
					<button type="button" className="widget-board-edit-btn" onClick={() => setEditMode(true)}>
						✎ Customize
					</button>
				) : (
					<button
						type="button"
						className="widget-board-edit-btn widget-board-done-btn"
						onClick={handleDone}
						disabled={saving}
					>
						{saving ? 'Saving…' : 'Done'}
					</button>
				)}
			</div>

			{loadError ? <div className="widget-inner-error">{loadError}</div> : null}

			{editMode ? (
				<>
					<p className="widget-picker-hint">
						Select the widgets you want displayed for this {contextType}.
					</p>
					<div className="widget-picker">
						{availableWidgets.map((entry) => {
							const active = activeIds.has(entry.manifest.id);
							return (
								<button
									key={entry.manifest.id}
									type="button"
									className={`widget-picker-card ${active ? 'is-active' : ''}`}
									onClick={() => toggleWidget(entry.manifest.id)}
								>
									<div className="widget-picker-check">{active ? '✓' : '+'}</div>
									<div className="widget-picker-name">{entry.manifest.name}</div>
									<div className="widget-picker-desc">{entry.manifest.description}</div>
									<div className="widget-picker-services">{entry.manifest.services.join(' · ')}</div>
								</button>
							);
						})}
					</div>
				</>
			) : layout.length === 0 ? (
				<div className="widget-board-empty">
					<p>No widgets configured for this {contextType} yet.</p>
					<button type="button" className="widget-board-edit-btn" onClick={() => setEditMode(true)}>
						+ Add widgets
					</button>
				</div>
			) : (
				<div className="widget-grid">
					{layout.map((entry) => {
						const reg = getWidget(entry.widgetId);
						if (!reg) return null;
						const { Component, manifest } = reg;
						return (
							<div
								key={entry.widgetId}
								className={`widget-card widget-card-${entry.size || manifest.defaultSize}`}
							>
								<div className="widget-card-header">
									<span className="widget-card-name">{manifest.name}</span>
									<div className="widget-card-header-actions">
										{manifest.configurable && reg.SettingsComponent ? (
											<button
												type="button"
												className="widget-gear-btn"
												aria-label={`${manifest.name} settings`}
												title={`${manifest.name} settings`}
												onClick={() => setSettingsWidgetId(entry.widgetId)}
											>
												⚙
											</button>
										) : null}
										<span className="widget-card-service">{manifest.services[0]}</span>
									</div>
								</div>
								<div className="widget-card-body">
									<Component
										contextType={contextType}
										contextId={contextId}
										ecosystemId={ecosystemId}
									/>
								</div>
							</div>
						);
					})}
				</div>
			)}

			{SettingsComponent ? (
				<SettingsComponent
					contextType={contextType}
					contextId={contextId}
					ecosystemId={ecosystemId}
					onClose={() => setSettingsWidgetId(null)}
				/>
			) : null}
		</div>
	);
}
